import { useCallback, useEffect, useMemo, useState } from 'react';
import './reports.scss';
import { useApiReports } from '../../api/api.reports';
import { ReportType } from '../../types/types.reports';
import { ModalReport } from '../../components/library/reporting/ModalReport';
import { LoadPanel, ScrollView } from 'devextreme-react';
import TextBox from 'devextreme-react/text-box';
import { ToolbarSimple } from '../../components';
import { useScreenSize } from '../../utils/media-query';
import { microscopie } from '../../images/icons';

const Index = () => {
  const [data, setData] = useState<any[]>([]);
  const { getReportsAndWorkList } = useApiReports();
  const [visible, setVisible] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState<ReportType>(ReportType.System);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const { isMedium, isSmall, isXSmall } = useScreenSize();

  useEffect(() => {
    setLoading(true);
    getReportsAndWorkList([])
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleVisible = useCallback(() => {
    setVisible(false);
  }, []);

  const handleCardClick = useCallback((report: any) => {
    setReportName(report.Rp_Nombre);
    setReportType(report.Rp_Tipo);
    setVisible(true);
  }, []);

  // Filtrar reportes por búsqueda - ahora reacciona en tiempo real
  const filteredData = useMemo(() => {
    if (!searchText) return data;
    const search = searchText.toLowerCase();
    return data.filter((item) => item.Rp_Nombre?.toLowerCase().includes(search));
  }, [data, searchText]);

  // Separar reportes por tipo
  const workLists = useMemo(() => filteredData.filter((item) => item.Rp_Tipo === ReportType.WorkList), [filteredData]);

  const reportsAndStats = useMemo(() => filteredData.filter((item) => item.Rp_Tipo === ReportType.Report), [filteredData]);

  // Función para obtener icono según el nombre del reporte
  const getReportIcon = (name: string): string => {
    const nameLower = name?.toLowerCase() || '';
    // Normalize to remove accents so 'estadística' -> 'estadistica'
    const normalized = nameLower.normalize ? nameLower.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : nameLower;
    if (normalized.includes('factura')) return 'money';
    if (normalized.includes('venta')) return 'cart';
    if (normalized.includes('estadist') || normalized.includes('total')) return 'chart';
    if (normalized.includes('examen')) return 'microscopie';
    if (normalized.includes('informe')) return 'doc';
    if (normalized.includes('vendedor')) return 'group';
    if (normalized.includes('domicilio')) return 'home';
    return 'doc';
  };

  // Determinar columnas según tamaño de pantalla - CORREGIDO
  const gridColumns = useMemo(() => {
    if (isXSmall) return 1;
    if (isSmall) return 1;
    if (isMedium) return 2;
    return 3; // isLarge o más grande = 3 columnas
  }, [isXSmall, isSmall, isMedium]);

  return (
    <div className='view crm-contact-list reports-page'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <ScrollView className='view-wrapper-scroll'>
          <ToolbarSimple title='Reportes y Estadísticas'>
            <LoadPanel visible={loading} />

            {/* Buscador */}
            <div className='reports-search'>
              <TextBox placeholder='Buscar reportes...' mode='search' value={searchText} onValueChanged={(e) => setSearchText(e.value || '')} width='100%' stylingMode='outlined' />
            </div>

            {/* Sección: Listas de Trabajo */}
            {workLists.length > 0 && (
              <div className='reports-section'>
                <div className='section-header'>
                  <h3 className='section-title'>Listas de Trabajo</h3>
                  <span className='section-count'>{workLists.length} disponibles</span>
                </div>
                <div className='reports-grid' style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}>
                  {workLists.map((report, index) => (
                    <div key={index} className='report-card' onClick={() => handleCardClick(report)}>
                      <div className='card-icon' style={{ backgroundColor: '#1976d2' }}>
                        {getReportIcon(report.Rp_Nombre) === 'microscopie' ? <img src={microscopie} alt='microscopio' /> : <i className={`dx-icon dx-icon-${getReportIcon(report.Rp_Nombre)}`}></i>}
                      </div>
                      <div className='card-content'>
                        <h4 className='card-title'>{report.Rp_Nombre}</h4>
                        <span className='card-badge badge-worklist'>Lista de trabajo</span>
                      </div>
                      <div className='card-action'>
                        <i className='dx-icon dx-icon-chevronright'></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sección: Reportes y Estadísticas */}
            {reportsAndStats.length > 0 && (
              <div className='reports-section'>
                <div className='section-header'>
                  <h3 className='section-title'>Reportes y Estadísticas</h3>
                  <span className='section-count'>{reportsAndStats.length} disponibles</span>
                </div>
                <div className='reports-grid' style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}>
                  {reportsAndStats.map((report, index) => (
                    <div key={index} className='report-card' onClick={() => handleCardClick(report)}>
                      <div className='card-icon' style={{ backgroundColor: '#388e3c' }}>
                        {getReportIcon(report.Rp_Nombre) === 'microscopie' ? <img src={microscopie} alt='microscopio' /> : <i className={`dx-icon dx-icon-${getReportIcon(report.Rp_Nombre)}`}></i>}
                      </div>
                      <div className='card-content'>
                        <h4 className='card-title'>{report.Rp_Nombre}</h4>
                        <span className='card-badge badge-report'>Informe</span>
                      </div>
                      <div className='card-action'>
                        <i className='dx-icon dx-icon-chevronright'></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ToolbarSimple>
        </ScrollView>
      </div>

      <ModalReport visible={visible} setVisible={handleVisible} reportName={reportName} type={reportType} />
    </div>
  );
};

export default Index;
